import { Fragment, useEffect } from 'react';
import {
  CatalogFilterLayout,
  EntityKindFilter,
  EntityKindPicker,
  EntityTypeFilter,
  EntityLifecyclePicker,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityProcessingStatusPicker,
  EntityTagPicker,
  EntityTypePicker,
  EntityListProvider,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { CatalogTable, CatalogTableColumnsFunc, CatalogTableRow } from '@backstage/plugin-catalog';
import { Content, TableColumn, TableProps } from '@backstage/core-components';
import {
  CatalogFeature,
  FeatureDefinition,
  FeatureEntry,
} from './catalogFeatures';
import { BusinessProductUnitFilter } from './customFilters/BusinessProductUnitFilter';

/**
 * Maps each CatalogFeature to its default enabled state and its rendered component.
 * Add new entries here as features are defined.
 */
const featureRegistry: Record<CatalogFeature, FeatureDefinition> = {
  [CatalogFeature.KindSelector]: {
    defaultEnabled: false,
    render: () => <EntityKindPicker />,
  },
  [CatalogFeature.TypeSelector]: {
    defaultEnabled: false,
    render: () => <EntityTypePicker />,
  },
  [CatalogFeature.Owned]: {
    defaultEnabled: true,
    render: () => <UserListPicker />,
  },
  [CatalogFeature.OwnerSelector]: {
    defaultEnabled: false,
    render: () => <EntityOwnerPicker />,
  },
  [CatalogFeature.BusinessProductUnitSelector]: {
    defaultEnabled: false,
    render: () => <BusinessProductUnitFilter />,
  },
  [CatalogFeature.LifecycleSelector]: {
    defaultEnabled: false,
    render: () => <EntityLifecyclePicker />,
  },
  [CatalogFeature.TagSelector]: {
    defaultEnabled: false,
    render: () => <EntityTagPicker />,
  },
  [CatalogFeature.ProcessingStatus]: {
    defaultEnabled: false,
    render: () => <EntityProcessingStatusPicker />,
  },
  [CatalogFeature.NamespaceSelector]: {
    defaultEnabled: false,
    render: () => <EntityNamespacePicker />,
  },
};

const featureOrder = Object.values(CatalogFeature);

function resolveEnabledFeatures(
  features?: FeatureEntry[],
): Record<CatalogFeature, boolean> {
  const enabled = Object.fromEntries(
    featureOrder.map(f => [f, featureRegistry[f].defaultEnabled]),
  ) as Record<CatalogFeature, boolean>;

  if (!features) {
    return enabled;
  }

  for (const entry of features) {
    if (typeof entry === 'string') {
      enabled[entry] = true;
    } else {
      for (const [feature, value] of Object.entries(entry) as [
        CatalogFeature,
        boolean,
      ][]) {
        enabled[feature] = value;
      }
    }
  }

  return enabled;
}

export type CatalogExplorerPageProps = {
  /**
   * Controls which filter features are active.
   *
   * - Pass a `CatalogFeature` to enable it (or keep it enabled if already default).
   * - Pass `{ [CatalogFeature.X]: false }` to disable a default-on feature.
   * - Omit entirely to use all default-enabled features.
   *
   * @example
   * features={[
   *   CatalogFeature.OwnerSelector,
   *   CatalogFeature.TagSelector,
   *   { [CatalogFeature.Owned]: false },
   * ]}
   */
  features?: FeatureEntry[];
  /** Optional fixed initial kind filter (e.g. `group` or `user`). */
  initialKind?: string;
  /** Optional fixed initial `spec.type` filter (e.g. `subscription`). */
  initialType?: string;
  /** Optional custom column set. Falls back to CatalogTable default columns when omitted. */
  columns?: TableColumn<CatalogTableRow>[] | CatalogTableColumnsFunc;
  /**
   * Optional custom row actions. Falls back to CatalogTable's default actions
   * (view, edit, star) when omitted.
   */
  actions?: TableProps<CatalogTableRow>['actions'];
};

const CatalogExplorerInitialFilters = ({
  initialKind,
  initialType,
}: {
  initialKind?: string;
  initialType?: string;
}) => {
  const {
    filters,
    updateFilters,
    queryParameters: { kind: kindQueryParam, type: typeQueryParam },
  } = useEntityList();

  const queryKind = [kindQueryParam].flat()[0];
  const queryType = [typeQueryParam].flat()[0];

  useEffect(() => {
    const update: Parameters<typeof updateFilters>[0] = {};

    if (initialKind) {
      const selectedKind = queryKind ?? filters.kind?.value;
      if (selectedKind !== initialKind) {
        update.kind = new EntityKindFilter(initialKind, initialKind);
      }
    }

    if (initialType) {
      const selectedType = queryType ?? filters.type?.value;
      if (selectedType !== initialType) {
        update.type = new EntityTypeFilter(initialType);
      }
    }

    if (Object.keys(update).length > 0) {
      updateFilters(update);
    }
  }, [
    initialKind,
    initialType,
    queryKind,
    queryType,
    filters.kind,
    filters.type,
    updateFilters,
  ]);

  return null;
};

export const CatalogExplorerPage = ({
  features,
  initialKind,
  initialType,
  columns,
  actions,
}: CatalogExplorerPageProps) => {
  const enabled = resolveEnabledFeatures(features);

  return (
    <Content>
      <EntityListProvider>
        <CatalogExplorerInitialFilters
          initialKind={initialKind}
          initialType={initialType}
        />
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            {featureOrder.map(feature =>
              enabled[feature] ? (
                <Fragment key={feature}>
                  {featureRegistry[feature].render()}
                </Fragment>
              ) : null,
            )}
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <CatalogTable columns={columns} actions={actions} />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </EntityListProvider>
    </Content>
  );
};
